/**
 * Reading a register that the server bounds.
 *
 * The API returns at most a page of rows plus the TRUE total (see
 * `apps/api/src/db/paging.ts`). Both halves matter here, and the second one is
 * the reason this file is not just `body.data`.
 *
 * Every register screen searches, filters and sorts IN THE BROWSER, across
 * whatever it has loaded. So a page that arrived looking like the whole
 * register would make the search box lie: "no matches" when the match is
 * sitting in rows nobody fetched. Nobody reports that as a bug, because it
 * looks like an answer. Carrying `total` beside `rows` is what lets the screen
 * say "the first 500 of 1,240" and offer the rest.
 */

import { request } from './client.js';

export interface RegisterPage<T> {
  readonly rows: readonly T[];
  /** How many exist altogether, ignoring the limit. */
  readonly total: number;
  /** True when `rows` is a prefix. The screen must say so. */
  readonly truncated: boolean;
}

/**
 * Ask for a register.
 *
 * `total` and `truncated` are read defensively. A route that has not been
 * given the bound yet still answers `{ data }`, and the honest reading of that
 * is "this is everything" — which is exactly what it was before. That is what
 * lets the registers be converted one at a time rather than in one commit.
 */
export async function pageCall<T>(path: string, limit?: number): Promise<RegisterPage<T>> {
  const url = limit === undefined ? path : `${path}${path.includes('?') ? '&' : '?'}limit=${limit}`;
  const body = await request<{
    data: readonly T[];
    total?: number;
    truncated?: boolean;
  }>(url);

  const rows = body.data;
  return {
    rows,
    total: body.total ?? rows.length,
    truncated: body.truncated ?? false,
  };
}

/**
 * What "load the rest" asks for.
 *
 * The server clamps this to its own ceiling, so it is a request and not a
 * promise — which is why the screen re-reads `truncated` from the answer
 * rather than assuming one click fetched everything.
 */
export const LOAD_ALL_LIMIT = 10_000;
